import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useLang, LangToggle } from '../LanguageContext';
import { t } from '../i18n';

const FILTRES_TYPE = ['Tous', 'Clients', 'Visites', 'Actions', 'Commerciaux'];
const FILTRES_STATUT = ['Tous', 'Client actif', 'Prospect', 'Client inactif', 'Nouveau client', 'Autre...'];
const FILTRES_PERIODE = ['Tout', 'Aujourd\'hui', 'Cette semaine', 'Ce mois', 'Autre...'];
const REGROUPEMENTS = ['Aucun', 'Par client', 'Par wilaya', 'Par résultat', 'Par date', 'Par commercial', 'Autre...'];

export default function RechercheGlobale({ user, isManager, onClose, onSelectClient, onSelectCommercial }) {
  const { lang, isRTL } = useLang();
  const [terme, setTerme] = useState('');
  const [donnees, setDonnees] = useState({ clients: [], visites: [], actions: [], commerciaux: [] });
  const [loading, setLoading] = useState(false);
  const [showFiltres, setShowFiltres] = useState(false);

  // Filtres
  const [filtreType, setFiltreType] = useState('Tous');
  const [filtreWilaya, setFiltreWilaya] = useState('Toutes');
  const [filtreStatut, setFiltreStatut] = useState('Tous');
  const [filtrePeriode, setFiltrePeriode] = useState('Tout');
  const [regroupement, setRegroupement] = useState('Aucun');

  // Filtres personnalisés
  const [filtresPerso, setFiltresPerso] = useState([]);
  const [showAddFiltre, setShowAddFiltre] = useState(false);
  const [newFiltre, setNewFiltre] = useState({ nom: '', champ: 'nom', valeur: '' });
  const [regroupementsPerso, setRegroupementsPerso] = useState([]);
  const [showAddRegroupement, setShowAddRegroupement] = useState(false);
  const [newRegroupement, setNewRegroupement] = useState({ nom: '', champ: 'wilaya' });

  const inputRef = useRef(null);

  useEffect(() => { chargerDonnees(); setTimeout(() => inputRef.current?.focus(), 100); }, []);

  const chargerDonnees = async () => {
    setLoading(true);
    try {
      let cSnap, vSnap, aSnap, uSnap;
      if (isManager) {
        cSnap = await getDocs(collection(db, 'clients'));
        vSnap = await getDocs(collection(db, 'visites'));
        aSnap = await getDocs(collection(db, 'actions'));
        uSnap = await getDocs(query(collection(db, 'users'), where('role', '==', 'commercial')));
      } else {
        cSnap = await getDocs(query(collection(db, 'clients'), where('commercialIds', 'array-contains', user.uid)));
        vSnap = await getDocs(query(collection(db, 'visites'), where('commercialId', '==', user.uid)));
        aSnap = await getDocs(query(collection(db, 'actions'), where('commercialId', '==', user.uid)));
        uSnap = { docs: [] };
      }
      setDonnees({
        clients: cSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        visites: vSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        actions: aSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        commerciaux: uSnap.docs.map(d => ({ id: d.id, ...d.data() })),
      });
    } catch (e) { console.log(e); }
    setLoading(false);
  };

  const wilayas = ['Toutes', ...new Set([
    ...donnees.clients.map(c => c.wilaya || c.commune || '').filter(Boolean)
  ])];

  const matchPeriode = (dateStr) => {
    if (filtrePeriode === 'Tout') return true;
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const now = new Date();
    if (filtrePeriode === 'Aujourd\'hui') return d.toDateString() === now.toDateString();
    if (filtrePeriode === 'Cette semaine') {
      const semDebut = new Date(now); semDebut.setDate(now.getDate() - now.getDay());
      return d >= semDebut;
    }
    if (filtrePeriode === 'Ce mois') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    return true;
  };

  const matchFiltresPerso = (item) => {
    return filtresPerso.every(f => {
      const val = (item[f.champ] || '').toString().toLowerCase();
      return val.includes(f.valeur.toLowerCase());
    });
  };

  // FILTRER
  const t = terme.toLowerCase();

  const clients = (filtreType === 'Tous' || filtreType === 'Clients') ? donnees.clients.filter(c => {
    const matchTerme = !terme || c.nom?.toLowerCase().includes(t) || c.commune?.toLowerCase().includes(t) || c.wilaya?.toLowerCase().includes(t) || c.telephone?.includes(t) || c.adresse?.toLowerCase().includes(t) || c.type?.toLowerCase().includes(t);
    const matchW = filtreWilaya === 'Toutes' || c.wilaya === filtreWilaya || c.commune === filtreWilaya;
    const matchS = filtreStatut === 'Tous' || c.type === filtreStatut || c.statut === filtreStatut;
    return matchTerme && matchW && matchS && matchFiltresPerso(c);
  }) : [];

  const visites = (filtreType === 'Tous' || filtreType === 'Visites') ? donnees.visites.filter(v => {
    const matchTerme = !terme || v.clientNom?.toLowerCase().includes(t) || v.resultat?.toLowerCase().includes(t) || v.objectifVisite?.toLowerCase().includes(t) || v.commentaire?.toLowerCase().includes(t) || v.commercialNom?.toLowerCase().includes(t);
    const matchP = matchPeriode(v.heureDebut);
    return matchTerme && matchP && matchFiltresPerso(v);
  }) : [];

  const actions = (filtreType === 'Tous' || filtreType === 'Actions') ? donnees.actions.filter(a => {
    const matchTerme = !terme || a.clientNom?.toLowerCase().includes(t) || a.action?.toLowerCase().includes(t) || a.responsable?.toLowerCase().includes(t);
    const matchP = matchPeriode(a.dateAction);
    return matchTerme && matchP && matchFiltresPerso(a);
  }) : [];

  const commerciaux = (filtreType === 'Tous' || filtreType === 'Commerciaux') && isManager ? donnees.commerciaux.filter(c => {
    return !terme || c.nom?.toLowerCase().includes(t) || c.prenom?.toLowerCase().includes(t);
  }) : [];

  // REGROUPER
  const regrouperPar = (items, champ) => {
    const groupes = {};
    items.forEach(item => {
      const key = item[champ] || 'Non renseigné';
      if (!groupes[key]) groupes[key] = [];
      groupes[key].push(item);
    });
    return groupes;
  };

  const getRegroupementActif = () => {
    if (regroupement === 'Aucun') return null;
    if (regroupement === 'Par client') return 'clientNom';
    if (regroupement === 'Par wilaya') return 'wilaya';
    if (regroupement === 'Par résultat') return 'resultat';
    if (regroupement === 'Par commercial') return 'commercialNom';
    if (regroupement === 'Par date') return null;
    const perso = regroupementsPerso.find(r => r.nom === regroupement);
    return perso?.champ || null;
  };

  const totalResultats = clients.length + visites.length + actions.length + commerciaux.length;
  const filtresActifs = [filtreType !== 'Tous', filtreWilaya !== 'Toutes', filtreStatut !== 'Tous', filtrePeriode !== 'Tout', filtresPerso.length > 0, regroupement !== 'Aucun'].filter(Boolean).length;

  const highlight = (text) => {
    if (!text || !terme) return text;
    const idx = text.toLowerCase().indexOf(t);
    if (idx === -1) return text;
    return <>{text.slice(0, idx)}<span style={{ background: '#FAEEDA', fontWeight: 600 }}>{text.slice(idx, idx + terme.length)}</span>{text.slice(idx + terme.length)}</>;
  };

  const renderClients = (liste) => liste.map(c => (
    <div key={c.id} style={styles.resultCard} onClick={() => onSelectClient && onSelectClient(c)}>
      <div style={styles.resultIcon}>👥</div>
      <div style={{ flex: 1 }}>
        <div style={styles.resultTitre}>{highlight(c.nom)}</div>
        <div style={styles.resultSub}>{c.type || c.statut} · {c.commune} · {c.wilaya}</div>
        {c.telephone && <div style={styles.resultSub}>📞 {c.telephone}</div>}
      </div>
      {c.gps && <span>📍</span>}
      <span style={styles.arrow}>→</span>
    </div>
  ));

  const renderVisites = (liste) => liste.map(v => (
    <div key={v.id} style={styles.resultCard}>
      <div style={styles.resultIcon}>📋</div>
      <div style={{ flex: 1 }}>
        <div style={styles.resultTitre}>{highlight(v.clientNom)}</div>
        <div style={styles.resultSub}>{highlight(v.resultat)}</div>
        {v.commentaire && <div style={styles.resultSub}>💬 {highlight(v.commentaire)}</div>}
        <div style={{ fontSize: 10, color: '#aaa', marginTop: 3 }}>{v.heureDebut ? new Date(v.heureDebut).toLocaleDateString('fr-FR') : ''} · {v.commercialNom}</div>
      </div>
    </div>
  ));

  const renderActions = (liste) => liste.map(a => (
    <div key={a.id} style={{ ...styles.resultCard, background: a.statut === 'A FAIRE' && a.dateAction && new Date(a.dateAction) < new Date() ? '#FCEBEB' : '#fff' }}>
      <div style={styles.resultIcon}>{a.statut === 'FAIT' ? '✅' : a.dateAction && new Date(a.dateAction) < new Date() ? '⚠️' : '📅'}</div>
      <div style={{ flex: 1 }}>
        <div style={styles.resultTitre}>{highlight(a.clientNom)}</div>
        <div style={styles.resultSub}>{highlight(a.action)} · {a.dateAction}</div>
      </div>
      <div style={{ fontSize: 10, padding: '2px 8px', borderRadius: 99, background: a.statut === 'FAIT' ? '#EAF3DE' : '#f5f5f5', color: a.statut === 'FAIT' ? '#3B6D11' : '#888' }}>
        {a.statut === 'FAIT' ? 'Fait' : 'À faire'}
      </div>
    </div>
  ));

  const renderSection = (label, items, renderFn) => {
    if (items.length === 0) return null;
    const champGroupe = getRegroupementActif();
    if (champGroupe && (label.includes('Visite') || label.includes('Action') || label.includes('Client'))) {
      const groupes = regrouperPar(items, champGroupe);
      return (
        <>
          <div style={styles.sectionLabel}>{label} ({items.length})</div>
          {Object.entries(groupes).map(([key, groupItems]) => (
            <div key={key}>
              <div style={styles.groupeLabel}>📂 {key} ({groupItems.length})</div>
              {renderFn(groupItems)}
            </div>
          ))}
        </>
      );
    }
    return (
      <>
        <div style={styles.sectionLabel}>{label} ({items.length})</div>
        {renderFn(items)}
      </>
    );
  };

  return (
    <div style={{ ...styles.app, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={styles.topbar}>
        <button onClick={onClose} style={styles.backBtn}>←</button>
        <div style={styles.searchWrap}>
          <span>🔍</span>
          <input ref={inputRef} style={styles.searchInput} placeholder="Chercher partout..." value={terme} onChange={e => setTerme(e.target.value)} autoFocus />
          {terme && <button style={styles.clearBtn} onClick={() => setTerme('')}>✕</button>}
        </div>
        <button style={{ ...styles.filtreBtn, ...(filtresActifs > 0 ? styles.filtreBtnActive : {}) }} onClick={() => setShowFiltres(!showFiltres)}>
          ⚙️{filtresActifs > 0 && <span style={styles.filtreBadge}>{filtresActifs}</span>}
        </button>
      </div>

      {/* PANNEAU FILTRES */}
      {showFiltres && (
        <div style={styles.filtresPanel}>
          <div style={styles.filtresGrid}>

            <div style={styles.filtreGroup}>
              <div style={styles.filtreLabel}>Type</div>
              <select style={styles.select} value={filtreType} onChange={e => setFiltreType(e.target.value)}>
                {FILTRES_TYPE.map(f => <option key={f}>{f}</option>)}
              </select>
            </div>

            <div style={styles.filtreGroup}>
              <div style={styles.filtreLabel}>Wilaya</div>
              <select style={styles.select} value={filtreWilaya} onChange={e => setFiltreWilaya(e.target.value)}>
                {wilayas.map(w => <option key={w}>{w}</option>)}
              </select>
            </div>

            <div style={styles.filtreGroup}>
              <div style={styles.filtreLabel}>Statut client</div>
              <select style={styles.select} value={filtreStatut} onChange={e => {
                if (e.target.value === 'Autre...') setShowAddFiltre(true);
                else setFiltreStatut(e.target.value);
              }}>
                {FILTRES_STATUT.map(f => <option key={f}>{f}</option>)}
              </select>
            </div>

            <div style={styles.filtreGroup}>
              <div style={styles.filtreLabel}>Période</div>
              <select style={styles.select} value={filtrePeriode} onChange={e => {
                if (e.target.value === 'Autre...') setShowAddFiltre(true);
                else setFiltrePeriode(e.target.value);
              }}>
                {FILTRES_PERIODE.map(f => <option key={f}>{f}</option>)}
              </select>
            </div>

            <div style={{ ...styles.filtreGroup, gridColumn: '1 / -1' }}>
              <div style={styles.filtreLabel}>Regrouper par</div>
              <select style={styles.select} value={regroupement} onChange={e => {
                if (e.target.value === 'Autre...') setShowAddRegroupement(true);
                else setRegroupement(e.target.value);
              }}>
                {[...REGROUPEMENTS, ...regroupementsPerso.map(r => r.nom)].map(r => <option key={r}>{r}</option>)}
              </select>
            </div>

          </div>

          {/* FILTRES PERSONNALISÉS */}
          {filtresPerso.length > 0 && (
            <div style={styles.filtresPersoWrap}>
              <div style={styles.filtreLabel}>Filtres personnalisés actifs</div>
              {filtresPerso.map((f, i) => (
                <div key={i} style={styles.filtrePersoTag}>
                  <span>{f.nom} : {f.valeur}</span>
                  <button style={styles.filtrePersoRemove} onClick={() => setFiltresPerso(fp => fp.filter((_, j) => j !== i))}>✕</button>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, padding: '8px 0 4px' }}>
            <button style={styles.addFiltreBtn} onClick={() => setShowAddFiltre(true)}>+ Filtre personnalisé</button>
            <button style={styles.addFiltreBtn} onClick={() => setShowAddRegroupement(true)}>+ Regroupement</button>
            <button style={{ ...styles.addFiltreBtn, color: '#C0392B' }} onClick={() => {
              setFiltreType('Tous'); setFiltreWilaya('Toutes'); setFiltreStatut('Tous');
              setFiltrePeriode('Tout'); setRegroupement('Aucun'); setFiltresPerso([]);
            }}>Réinitialiser</button>
          </div>
        </div>
      )}

      {/* MODAL FILTRE PERSONNALISÉ */}
      {showAddFiltre && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalTitre}>Créer un filtre personnalisé</div>
            <div style={{ marginBottom: 10 }}>
              <div style={styles.filtreLabel}>Nom du filtre</div>
              <input style={styles.input} placeholder="Ex: Clients fort potentiel" value={newFiltre.nom} onChange={e => setNewFiltre(f => ({ ...f, nom: e.target.value }))} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={styles.filtreLabel}>Champ à filtrer</div>
              <select style={styles.select} value={newFiltre.champ} onChange={e => setNewFiltre(f => ({ ...f, champ: e.target.value }))}>
                {['nom', 'wilaya', 'commune', 'type', 'statut', 'potentiel', 'resultat', 'action', 'responsable', 'commercialNom'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={styles.filtreLabel}>Valeur</div>
              <input style={styles.input} placeholder="Ex: Fort" value={newFiltre.valeur} onChange={e => setNewFiltre(f => ({ ...f, valeur: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={styles.btnCancel} onClick={() => setShowAddFiltre(false)}>Annuler</button>
              <button style={styles.btnSave} onClick={() => {
                if (newFiltre.nom && newFiltre.valeur) {
                  setFiltresPerso(fp => [...fp, { ...newFiltre }]);
                  setNewFiltre({ nom: '', champ: 'nom', valeur: '' });
                  setShowAddFiltre(false);
                }
              }}>Ajouter</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL REGROUPEMENT PERSONNALISÉ */}
      {showAddRegroupement && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalTitre}>Créer un regroupement personnalisé</div>
            <div style={{ marginBottom: 10 }}>
              <div style={styles.filtreLabel}>Nom du regroupement</div>
              <input style={styles.input} placeholder="Ex: Par potentiel" value={newRegroupement.nom} onChange={e => setNewRegroupement(r => ({ ...r, nom: e.target.value }))} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={styles.filtreLabel}>Champ de regroupement</div>
              <select style={styles.select} value={newRegroupement.champ} onChange={e => setNewRegroupement(r => ({ ...r, champ: e.target.value }))}>
                {['wilaya', 'commune', 'type', 'statut', 'potentiel', 'resultat', 'responsable', 'commercialNom', 'objectifVisite'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={styles.btnCancel} onClick={() => setShowAddRegroupement(false)}>Annuler</button>
              <button style={styles.btnSave} onClick={() => {
                if (newRegroupement.nom) {
                  setRegroupementsPerso(rp => [...rp, { ...newRegroupement }]);
                  setRegroupement(newRegroupement.nom);
                  setNewRegroupement({ nom: '', champ: 'wilaya' });
                  setShowAddRegroupement(false);
                }
              }}>Ajouter</button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.content}>
        {totalResultats === 0 && !terme && filtresActifs === 0 && (
          <div style={styles.hint}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🔍</div>
            <div style={{ color: '#888', marginBottom: 12 }}>Tapez pour chercher ou utilisez les filtres</div>
            <div style={styles.suggestions}>
              {['Constantine', 'Commande passée', 'Rappeler', 'Client actif', 'En retard'].map(s => (
                <button key={s} style={styles.suggBtn} onClick={() => setTerme(s)}>{s}</button>
              ))}
            </div>
          </div>
        )}

        {(terme || filtresActifs > 0) && totalResultats === 0 && (
          <div style={styles.hint}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>😕</div>
            <div style={{ color: '#888' }}>Aucun résultat</div>
            <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>Modifiez vos critères de recherche</div>
          </div>
        )}

        {renderSection('👥 Clients', clients, renderClients)}
        {renderSection('📋 Visites', visites, renderVisites)}
        {renderSection('📅 Actions', actions, renderActions)}
        {commerciaux.length > 0 && (
          <>
            <div style={styles.sectionLabel}>👤 Commerciaux ({commerciaux.length})</div>
            {commerciaux.map(c => (
              <div key={c.id} style={styles.resultCard} onClick={() => onSelectCommercial && onSelectCommercial(c)}>
                <div style={styles.resultIcon}>👤</div>
                <div style={{ flex: 1 }}>
                  <div style={styles.resultTitre}>{highlight(c.prenom)} {highlight(c.nom)}</div>
                  <div style={styles.resultSub}>{(c.wilayas || []).join(' · ')}</div>
                </div>
                <span style={styles.arrow}>→</span>
              </div>
            ))}
          </>
        )}

        {totalResultats > 0 && (
          <div style={styles.totalLine}>{totalResultats} résultat(s) {terme ? `pour "${terme}"` : ''}</div>
        )}
      </div>
    </div>
  );
}

const styles = {
  app: { maxWidth: 480, margin: '0 auto', minHeight: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' },
  topbar: { background: '#1C2833', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 },
  backBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32, color: '#fff', fontSize: 16, cursor: 'pointer', flexShrink: 0 },
  searchWrap: { flex: 1, display: 'flex', alignItems: 'center', background: '#fff', borderRadius: 10, padding: '8px 12px', gap: 8 },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: 15, fontFamily: 'system-ui', background: 'transparent' },
  clearBtn: { background: 'none', border: 'none', color: '#aaa', fontSize: 14, cursor: 'pointer' },
  filtreBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, width: 36, height: 36, fontSize: 16, cursor: 'pointer', color: '#fff', position: 'relative', flexShrink: 0 },
  filtreBtnActive: { background: '#C0392B' },
  filtreBadge: { position: 'absolute', top: -3, right: -3, background: '#F7DC6F', color: '#1C2833', fontSize: 9, width: 14, height: 14, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 },
  filtresPanel: { background: '#fff', padding: '12px', borderBottom: '1px solid #eee' },
  filtresGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 },
  filtreGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
  filtreLabel: { fontSize: 11, fontWeight: 500, color: '#888', textTransform: 'uppercase', letterSpacing: 0.3 },
  select: { padding: '7px 8px', border: '1px solid #ddd', borderRadius: 8, fontSize: 12, background: '#f9f9f9', width: '100%' },
  filtresPersoWrap: { display: 'flex', flexWrap: 'wrap', gap: 6, margin: '8px 0' },
  filtrePersoTag: { display: 'flex', alignItems: 'center', gap: 6, background: '#E6F1FB', borderRadius: 99, padding: '4px 10px', fontSize: 12, color: '#185FA5' },
  filtrePersoRemove: { background: 'none', border: 'none', color: '#185FA5', cursor: 'pointer', fontSize: 12 },
  addFiltreBtn: { padding: '6px 12px', border: '1px dashed #ddd', borderRadius: 8, background: 'none', color: '#666', fontSize: 11, cursor: 'pointer' },
  content: { flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: 8 },
  hint: { textAlign: 'center', padding: '2rem 1rem', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  suggestions: { display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center', marginTop: 12 },
  suggBtn: { padding: '6px 14px', border: '1px solid #ddd', borderRadius: 99, background: '#fff', color: '#666', fontSize: 12, cursor: 'pointer' },
  sectionLabel: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4 },
  groupeLabel: { fontSize: 12, fontWeight: 600, color: '#185FA5', padding: '6px 10px', background: '#E6F1FB', borderRadius: 8, marginBottom: 4 },
  resultCard: { background: '#fff', borderRadius: 10, padding: '12px', display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer' },
  resultIcon: { fontSize: 20, flexShrink: 0 },
  resultTitre: { fontSize: 13, fontWeight: 600, color: '#1C2833' },
  resultSub: { fontSize: 11, color: '#888', marginTop: 2 },
  arrow: { fontSize: 14, color: '#ccc', flexShrink: 0 },
  totalLine: { textAlign: 'center', fontSize: 12, color: '#aaa', padding: '8px 0' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'flex-end' },
  modal: { background: '#fff', width: '100%', maxWidth: 480, margin: '0 auto', borderRadius: '20px 20px 0 0', padding: '20px 16px 30px' },
  modalTitre: { fontSize: 15, fontWeight: 600, color: '#1C2833', marginBottom: 14 },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, fontFamily: 'system-ui' },
  btnCancel: { flex: 1, padding: 11, border: '1px solid #ddd', borderRadius: 10, background: '#fff', color: '#666', fontSize: 13, cursor: 'pointer' },
  btnSave: { flex: 2, padding: 11, background: '#1C2833', border: 'none', borderRadius: 10, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};
